import React, { useState, useEffect, useRef } from 'react';
import Select from 'react-select';
import { useDispatch, useSelector } from 'react-redux';
import { EditorState } from 'draft-js';
import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQuery } from '@apollo/client';
import {
  Modal,
  ModalBody,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  FormFeedback,
  CustomInput,
  Row,
  Col
} from 'reactstrap';

import { GQL_GET_GENRES, GQL_PAYMENTLINK_CREATE_BY_SLUG, GQL_PAYMENTLINK_UPDATE_BY_SLUG } from '../../gqls';
import {
  validator,
  paymentLinkUrl,
  sanitizeDOM,
  REGEX_SLUG,
  REGEX_BAHASA,
  paymentTypeOptions,
  slug,
  writingPaymentMethodOptions,
  comicTypeOptions,
  readerOrientationOptions,
  readerDirectionOptions,
  toCapitalizeEachText
} from '../../utils';
import { updatePaymentLinks } from '../../actions/payment-links';
import { CustomStyles, CustomStylesInvalid } from '../custom-styles/react-select-styles';
import Icon from '../icon';
import DatePicker from '../date-time-picker';
import SubmitButton from '../submit-button';
import RichEditor, { getPlainText, getContentString, getEditorContent } from '../rich-text-editor';
import MultipleImageVideoInput from '../multiple-image-video-input';
import MayarAlert from '../alert/alert';

const ModalComic = ({ isOpen = false, onClose, onSuccess, data }) => {
  if (!isOpen) return null;
  const isUpdate = !!data?.id;
  const dispatch = useDispatch();
  const multipleCoverContent = useSelector(state => state.multipleCoverContent);
  const [isolatedMultipleCoverContent, setIsolatedMultipleCoverContent] = useState([]); // state for modal create product only


  const submitElement = useRef(null);
  const { register, handleSubmit, errors, control, setValue, watch } = useForm();
  const stateEdit = EditorState.createEmpty();
  const [editorState, setEditorState] = useState(stateEdit);
  const [multipleImageId, setMultipleImageId] = useState('');
  const [deletedCoverContents, setDeletedCoverContents] = useState([]);

  const [disableSubmitButton, setDisableSubmitButton] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [status, setStatus] = useState(data?.status || 'active');
  const [enableAffiliate, setEnableAffiliate] = useState(Boolean(data?.affiliateCommission));
  const [genreOptions, setGenreOptions] = useState([]);

  const { writingType, writingPaymentMethod, readerOrientation, link, paymentType, genreId } = watch();

  useQuery(GQL_GET_GENRES, {
    variables: { search: { name: [{ operator: 'notEmpty' }], type: [{ operator: 'eq', value: 'COMIC' }] } },
    fetchPolicy: 'network-only',
    onCompleted: result => {
      const options = [
        ...result?.getGenreFilteredList?.map(i => ({
          value: i.id,
          label: toCapitalizeEachText(i.name)
        })),
        {
          value: 'other',
          label: 'Lainnya (isi di bawah)'
        }
      ];
      setGenreOptions(options);
      if (isUpdate) {
        const defaultGenreId = options?.find(
          obj => obj.value === (data?.metadata?.customGenre ? 'other' : data?.metadata?.genreId)
        );
        setValue('genreId', defaultGenreId);
      }
    }
  });

  const [insertPaymentLinkBySlug, { loading: insertLoading }] = useMutation(GQL_PAYMENTLINK_CREATE_BY_SLUG);
  const [editPaymentLinkBySlug, { loading: updateLoading }] = useMutation(GQL_PAYMENTLINK_UPDATE_BY_SLUG);

  const onEditorStateChange = content => {
    setEditorState(content);

    register('description');
    setValue('description', sanitizeDOM(getContentString(content)));
    register('textDescription');
    setValue('textDescription', getPlainText(content));
  };

  const onSubmit = async values => {
    if (values.pages) values.pages = parseInt(values.pages);
    if (values.amount) values.amount = parseFloat(values.amount);
    if (values.paymentType === 'free') {
      values.amount = 0;
    } else if (values.paymentType === 'voluntary' && !values.amount) values.amount = 0;

    values.limit = !values.limit ? null : parseInt(values.limit);
    values.crossoutPrice = values.crossoutPrice ? parseFloat(values.crossoutPrice) : null;

    const metadata = {
      pages: values.pages || null,
      author: values.author,
      writer: values.writer,
      artist: values.artist,
      language: values.language,
      publishedAt: values.publishedAt,
      genreId: values.genreId?.value === 'other' ? null : values.genreId?.value,
      customGenre: values.genreId?.value === 'other' ? values.customGenre : null
    };
    delete values.author;
    delete values.writer;
    delete values.artist;
    delete values.pages;
    delete values.language;
    delete values.publishedAt;
    delete values.genreId;
    delete values.customGenre;
    delete values.textDescription;

    if (values.affiliateCommission) values.affiliateCommission = parseInt(values.affiliateCommission);
    else values.affiliateCommission = null;

    try {
      if (isUpdate) {
        delete values.writingType;
        delete values.writingPaymentMethod;
        values.id = data.id;
        await editPaymentLinkBySlug({
          variables: {
            input: {
              ...values,
              metadata,
              status,
              description: sanitizeDOM(values.description),
              ...(multipleImageId && { multipleImageId }),
              ...(deletedCoverContents.length && {
                deletedCoverContents: deletedCoverContents.map(item => {
                  if ((item.file?.mimetype || '').includes('video') || item?.videoId || item?.value) {
                    return {
                      videoMultipleFileId: item?.id
                    };
                  }
                  return {
                    imageId: item.editImage?.id || item.id
                  };
                })
              }),
              ...(multipleCoverContent.length && {
                orderedMultipleCoverContent: multipleCoverContent.map(item => {
                  if ((item.file?.mimetype || item.file?.type || '').includes('video') || item?.videoId || item?.value) {
                    return {
                      position: Number(item.position),
                      videoId: item.videoId || item.file?.id || item.value,
                      editId: item?.id
                    };
                  }
                  return {
                    position: Number(item.position),
                    imageId: item.editImage?.id || item.id
                  };
                })
              })
            }
          }
        });

        dispatch(updatePaymentLinks(Math.random()));
      } else {
        const newPL = await insertPaymentLinkBySlug({
          variables: {
            input: {
              ...values,
              metadata,
              link: slug(values.name),
              type: 'comic',
              status,
              description: sanitizeDOM(values.description),
              ...(multipleImageId && { multipleImageId }),
              ...(deletedCoverContents.length && {
                deletedCoverContents: deletedCoverContents.map(item => {
                  if ((item.file?.mimetype || '').includes('video') || item?.videoId || item?.value) {
                    return {
                      videoMultipleFileId: item?.id
                    };
                  }
                  return {
                    imageId: item.editImage?.id || item.id
                  };
                })
              }),
              ...(isolatedMultipleCoverContent.length && {
                orderedMultipleCoverContent: isolatedMultipleCoverContent.map(item => {
                  if ((item.file?.mimetype || item.file?.type || '').includes('video') || item?.videoId || item?.value) {
                    return {
                      position: Number(item.position),
                      videoId: item.videoId || item.file?.id || item.value,
                      editId: item?.id
                    };
                  }
                  return {
                    position: Number(item.position),
                    imageId: item.editImage?.id || item.id
                  };
                })
              })
            }
          }
        });

        if (newPL.data?.insertPaymentLinkBySlug.errorMessage) {
          throw new Error(newPL.data?.insertPaymentLinkBySlug.errorMessage || 'Terjadi kesalahan saat membuat produk');
        }


        if (newPL?.data?.insertPaymentLinkBySlug?.id) {
          onSuccess(newPL.data.insertPaymentLinkBySlug.id);
        }
        //segment tracking
        window.analytics.track('Create Komik/Galeri', values);
      }
    } catch (error) {
      setSubmitError(error.message || "Error! Silahkan coba lagi atau hubungi CS");
      window.analytics.track('Create Komik/Galeri - ERROR', values);
    }
  };

  const onHandleSaveAsDraft = () => {
    setStatus('closed');
    submitElement.current.click();
  };

  const isCouldHavePrice = writingType === 'ONE_SHOT' || ['PACKAGE', 'ALL'].includes(writingPaymentMethod);
  const priceWordingChapter = writingPaymentMethod === 'ALL' ? 'Satu Paket' : 'Semua Chapter';

  useEffect(() => {
    if (isUpdate) {
      const content = getEditorContent(data?.description);
      onEditorStateChange(content);
      register('writingType');
      register('writingPaymentMethod');
      setValue('writingType', data?.writingType);
      setValue('writingPaymentMethod', data?.writingPaymentMethod);
    }
  }, [isUpdate, data]);

  return (
    <Modal isOpen={isOpen} fade>
      <div className="modal-header">
        <h5 className="modal-title h2">{isUpdate ? 'Edit' : 'Buat'} Komik/Galeri</h5>
        <Button className="close" color="" onClick={onClose}>
          <Icon name="x" />
        </Button>
      </div>
      <ModalBody>
        <p>
          <small>Penjualan komik semakin mudah dengan fitur ini. Isi form di bawah ini untuk membuat komik baru.</small>
        </p>
        <hr />
        {submitError && <MayarAlert isError message={submitError} />}

        <Form onSubmit={handleSubmit(onSubmit)} id="create-payment-link">
          <FormGroup>
            <Label for="name">Nama Produk*</Label>
            <Input
              type="text"
              name="name"
              id="name"
              invalid={errors.name}
              defaultValue={data?.name}
              innerRef={register({
                required: validator.required,
                minLength: validator.minLength(5)
              })}
            />
            <FormFeedback>{errors.name?.message}</FormFeedback>
          </FormGroup>
          {isUpdate ? (
            <FormGroup>
              <Label for="link">Slug*</Label>
              <Input
                type="text"
                name="link"
                id="link"
                invalid={errors.link}
                defaultValue={data?.link}
                innerRef={register({
                  required: validator.required,
                  pattern: validator.pattern(REGEX_SLUG)
                })}
              />
              <FormFeedback>{errors.link?.message}</FormFeedback>
              <small>{'contoh : ' + paymentLinkUrl(link)}</small>
            </FormGroup>
          ) : (
            <>
              <FormGroup>
                <Label for="writingType">Tipe Tulisan</Label>
                <Controller
                  name="writingType"
                  control={control}
                  defaultValue={data?.writingType}
                  rules={{ required: validator.required }}
                  render={({ onChange, name, value }) => (
                    <Select
                      id={name}
                      name={name}
                      options={comicTypeOptions}
                      defaultValue={comicTypeOptions?.find(obj => obj.value === value)}
                      placeholder="Pilih Tipe Komik"
                      styles={!!errors.writingType ? CustomStylesInvalid : CustomStyles}
                      className={`${!!errors.writingType ? 'is-invalid' : ''}`}
                      onChange={({ value: v }) => onChange(v)}
                    />
                  )}
                />
                <FormFeedback>{errors.writingType?.message}</FormFeedback>
              </FormGroup>

              {/* JIKA TIDAK GRATIS DAN JIKA PILIH TIPE CHAPTER, MAKA MUNCUL MEKANISME BAYAR */}
              {writingType === 'CHAPTER' && (
                <FormGroup>
                  <Label for="writingPaymentMethod">Mekanisme Bayar</Label>
                  <Controller
                    name="writingPaymentMethod"
                    control={control}
                    defaultValue={data?.writingPaymentMethod}
                    rules={{ required: validator.required }}
                    render={({ onChange, name, value }) => (
                      <Select
                        id={name}
                        name={name}
                        options={writingPaymentMethodOptions}
                        defaultValue={writingPaymentMethodOptions?.find(obj => obj.value === value)}
                        placeholder="Pilih Mekanisme Bayar"
                        styles={!!errors.writingPaymentMethod ? CustomStylesInvalid : CustomStyles}
                        className={`${!!errors.writingPaymentMethod ? 'is-invalid' : ''}`}
                        onChange={({ value: v }) => onChange(v)}
                      />
                    )}
                  />
                  <FormFeedback>{errors.writingPaymentMethod?.message}</FormFeedback>
                </FormGroup>
              )}
            </>
          )}
          {isCouldHavePrice && (
            <>
              <FormGroup>
                <Label for="paymentType">Tipe Pembayaran</Label>
                <Controller
                  name="paymentType"
                  control={control}
                  defaultValue={data?.paymentType}
                  render={({ onChange, name, value: defaultPaymentType }) => (
                    <Select
                      id={name}
                      name={name}
                      options={paymentTypeOptions}
                      defaultValue={paymentTypeOptions.find(obj => obj.value === defaultPaymentType)}
                      placeholder="Pilih Tipe Pembayaran"
                      styles={!!errors.paymentType ? CustomStylesInvalid : CustomStyles}
                      className={`${!!errors.paymentType ? 'is-invalid' : ''}`}
                      onChange={({ value }) => onChange(value)}
                    />
                  )}
                />
              </FormGroup>
              {paymentType === 'paid' && (
                <FormGroup>
                  <Label for="amount">Harga{writingType === 'CHAPTER' && ' ' + priceWordingChapter}*</Label>
                  <InputGroup>
                    <InputGroupAddon addonType="prepend">
                      <InputGroupText>Rp</InputGroupText>
                    </InputGroupAddon>
                    <Input
                      type="number"
                      name="amount"
                      id="amount"
                      invalid={errors.amount}
                      defaultValue={data?.amount}
                      innerRef={register({
                        required: validator.required,
                        min: validator.min(1000),
                        max: validator.max(100_000_000)
                      })}
                    />
                    <FormFeedback>{errors.amount?.message}</FormFeedback>
                  </InputGroup>
                  <small>Penagihan ini menggunakan mata uang IDR (Rupiah)</small>
                  <br />
                  {writingPaymentMethod === 'PACKAGE' && (
                    <small>Harga Per-chapter dapat anda atur saat pembuatan chatper nanti</small>
                  )}
                </FormGroup>
              )}
              {paymentType === 'voluntary' && (
                <FormGroup>
                  <Label for="amount">Minimal Harga</Label>
                  <InputGroup>
                    <InputGroupAddon addonType="prepend">
                      <InputGroupText>Rp</InputGroupText>
                    </InputGroupAddon>
                    <Input
                      type="number"
                      name="amount"
                      id="amount"
                      invalid={errors.amount}
                      placeholder="0+"
                      defaultValue={data?.amount}
                      innerRef={register({
                        min: validator.min(0),
                        max: validator.max(100000000)
                      })}
                    />
                    <FormFeedback>{errors.amount?.message}</FormFeedback>
                  </InputGroup>
                  <small>Penagihan ini menggunakan mata uang IDR (Rupiah)</small>
                  <br />
                  {writingPaymentMethod === 'PACKAGE' && (
                    <small>Harga Per-chapter dapat anda atur saat pembuatan chatper nanti</small>
                  )}
                </FormGroup>
              )}
            </>
          )}

          <FormGroup>
            <Label for="textDescription">Deskripsi*</Label>
            <Controller
              name="textDescription"
              id="textDescription"
              control={control}
              defaultValue=""
              rules={{ required: { value: true, message: 'Wajib diisi' }, minLength: validator.minLength(5) }}
              render={({ onChange }) => (
                <RichEditor
                  onEditorStateChange={content => {
                    onChange(getPlainText(content));
                    onEditorStateChange(content);
                  }}
                  editorState={editorState}
                  invalidState={!!errors.textDescription}
                />
              )}
            />
            <FormFeedback className={`${!!errors.textDescription ? 'd-block' : ''}`}>
              {errors.textDescription?.message}
            </FormFeedback>
          </FormGroup>

          <MultipleImageVideoInput
            productName={data?.name}
            setMultipleImageId={setMultipleImageId}
            multipleImage={data?.multipleImage}
            onLoading={setDisableSubmitButton}
            deletedCoverContents={deletedCoverContents}
            setDeletedCoverContents={setDeletedCoverContents}
            isAutoUploadOnSelectFile={false}
            maxVideo={1}
            maxImage={5}
            isolatedMultipleCoverContent={!isUpdate && isolatedMultipleCoverContent} // use isolated state instead to avoid mixing value when visiting product detail page
            setIsolatedMultipleCoverContent={!isUpdate && setIsolatedMultipleCoverContent}
          />

          <FormGroup>
            <Label for="startAt">Waktu Mulai Penjualan</Label>
            <div className="mb-1">
              <Controller
                name="startAt"
                control={control}
                defaultValue={data?.startAt || null}
                render={({ onChange, name, value }) => (
                  <DatePicker
                    id={name}
                    placeholderText="Pilih Tanggal Mulai"
                    showTimeSelect
                    dateFormat="yyyy/MM/dd HH:mm"
                    timeFormat="HH:mm"
                    selected={value ? new Date(value) : null}
                    minDate={new Date()}
                    className={`rui-datetimepicker form-control w-auto mb-1 ${!!errors.startAt ? 'is-invalid' : ''}`}
                    onChange={onChange}
                  />
                )}
              />
            </div>
            <FormFeedback className="d-block">{errors.startAt?.message}</FormFeedback>
            <small>
              Kami akan membuka link pembayaran pada tanggal dan waktu yang anda pilih. Opsional, kosongkan untuk
              langsung membuka penjualan
            </small>
          </FormGroup>
          <FormGroup>
            <Label for="expiredAt">Tanggal Kadaluarsa</Label>
            <div className="mb-1">
              <Controller
                name="expiredAt"
                control={control}
                defaultValue={data?.expiredAt || null}
                render={({ onChange, name, value }) => (
                  <DatePicker
                    id={name}
                    placeholderText="Pilih tanggal atau kosongkan"
                    dateFormat="yyyy/MM/dd"
                    selected={value ? new Date(value) : null}
                    minDate={new Date().setDate(new Date().getDate() + 1)}
                    className={`rui-datetimepicker form-control w-auto mb-1 ${!!errors.expiredAt ? 'is-invalid' : ''}`}
                    onChange={onChange}
                  />
                )}
              />
            </div>
            <FormFeedback className="d-block">{errors.expiredAt?.message}</FormFeedback>
            <small>Kami akan menutup link pembayaran pada tanggal ini (opsional)</small>
          </FormGroup>
          <FormGroup>
            <Label for="notes">Catatan</Label>
            <Input
              type="textarea"
              name="notes"
              id="notes"
              invalid={errors.notes}
              defaultValue={data?.notes}
              innerRef={register()}
            />
            <FormFeedback>{errors.notes?.message}</FormFeedback>
            <small>
              Catatan akan dilihat oleh pendaftar/pembeli setelah melakukan pendaftaran/membayar (opsional).{' '}
            </small>
          </FormGroup>
          {((writingType === 'CHAPTER' && writingPaymentMethod === 'ALL') || writingType === 'ONE_SHOT') && (
            <FormGroup>
              <Label for="limit">Maksimum Jumlah Pembayaran (Kuota / QTY)</Label>
              <Input
                type="number"
                name="limit"
                id="limit"
                invalid={errors.limit}
                defaultValue={data?.limit}
                innerRef={register()}
              />
              <FormFeedback>{errors?.limit?.message}</FormFeedback>
              <small>
                Kami akan menutup link pembayaran setelah melewati batas jumlah maksimal. Kosongkan untuk tanpa limit
                jumlah (unlimited)
              </small>
            </FormGroup>
          )}
          <FormGroup>
            <Label for="readerOrientation">Tipe Pembaca</Label>
            <Controller
              name="readerOrientation"
              control={control}
              defaultValue={data?.readerOrientation}
              rules={{ required: validator.required }}
              render={({ onChange, name, value }) => (
                <Select
                  id={name}
                  name={name}
                  options={readerOrientationOptions}
                  defaultValue={readerOrientationOptions?.find(obj => obj.value === value)}
                  placeholder="Pilih Tipe Pembaca"
                  styles={!!errors.readerOrientation ? CustomStylesInvalid : CustomStyles}
                  className={`${!!errors.readerOrientation ? 'is-invalid' : ''}`}
                  onChange={({ value: v }) => onChange(v)}
                />
              )}
            />
            <FormFeedback>{errors.readerOrientation?.message}</FormFeedback>
          </FormGroup>
          {readerOrientation === 'HORIZONTAL' && (
            <FormGroup>
              <Label for="readerDirection">Arah Pembaca</Label>
              <Controller
                name="readerDirection"
                control={control}
                defaultValue={data?.readerDirection}
                rules={{ required: validator.required }}
                render={({ onChange, name, value }) => (
                  <Select
                    id={name}
                    name={name}
                    options={readerDirectionOptions}
                    defaultValue={readerDirectionOptions?.find(obj => obj.value === value)}
                    placeholder="Pilih Arah Pembaca"
                    styles={!!errors.readerDirection ? CustomStylesInvalid : CustomStyles}
                    className={`${!!errors.readerDirection ? 'is-invalid' : ''}`}
                    onChange={({ value: v }) => onChange(v)}
                  />
                )}
              />
              <FormFeedback>{errors.readerDirection?.message}</FormFeedback>
            </FormGroup>
          )}

          <hr />
          <h4 className="mt-15">Detail Tambahan (opsional)</h4>

          {genreOptions?.length && (
            <FormGroup>
              <Label for="genreId">Genre</Label>
              <Controller
                name="genreId"
                control={control}
                render={({ onChange, name, value }) => (
                  <Select
                    id={name}
                    name={name}
                    options={genreOptions}
                    value={value}
                    placeholder="Pilih Genre Komik"
                    styles={!!errors.genreId ? CustomStylesInvalid : CustomStyles}
                    className={`${!!errors.genreId ? 'is-invalid' : ''}`}
                    onChange={onChange}
                  />
                )}
              />
            </FormGroup>
          )}
          {genreId?.value === 'other' && (
            <FormGroup>
              <Label for="customGenre">Genre Lainnya*</Label>
              <Input
                type="text"
                name="customGenre"
                id="customGenre"
                invalid={errors.customGenre}
                defaultValue={data?.metadata?.customGenre}
                innerRef={register({
                  required: validator.required,
                  minLength: validator.minLength(5)
                })}
              />
              <small>Silahkan isi genre lainnya</small>
              <FormFeedback>{errors.customGenre?.message}</FormFeedback>
            </FormGroup>
          )}
          <FormGroup>
            <Label for="author">Author</Label>
            <Input
              type="text"
              name="author"
              id="author"
              invalid={errors.author}
              defaultValue={data?.metadata?.author}
              innerRef={register({
                minLength: validator.minLength(5)
              })}
            />
            <FormFeedback>{errors.author?.message}</FormFeedback>
          </FormGroup>
          <FormGroup>
            <Label for="writer">Penulis</Label>
            <Input
              type="text"
              name="writer"
              id="writer"
              invalid={errors.writer}
              defaultValue={data?.metadata?.writer}
              innerRef={register({
                minLength: validator.minLength(5)
              })}
            />
            <FormFeedback>{errors.writer?.message}</FormFeedback>
          </FormGroup>
          <FormGroup>
            <Label for="artist">Artis</Label>
            <Input
              type="text"
              name="artist"
              id="artist"
              invalid={errors.artist}
              defaultValue={data?.metadata?.artist}
              innerRef={register({
                minLength: validator.minLength(5)
              })}
            />
            <FormFeedback>{errors.artist?.message}</FormFeedback>
          </FormGroup>

          <FormGroup>
            <Label for="language">Bahasa</Label>
            <Input
              type="text"
              name="language"
              id="language"
              invalid={errors.language}
              defaultValue={data?.metadata?.language}
              innerRef={register({
                minLength: validator.minLength(4),
                pattern: validator.pattern(REGEX_BAHASA)
              })}
            />
            <FormFeedback>{errors.language?.message}</FormFeedback>
          </FormGroup>
          <FormGroup>
            <Label for="pages">Jumlah Halaman</Label>
            <Input
              type="number"
              name="pages"
              id="pages"
              invalid={errors.pages}
              defaultValue={data?.metadata?.pages}
              innerRef={register({})}
            />
            <FormFeedback>{errors.pages?.message}</FormFeedback>
          </FormGroup>
          <FormGroup>
            <Label for="publishedAt">Tanggal Publish</Label>
            <div className="mb-1">
              <Controller
                name="publishedAt"
                control={control}
                defaultValue={data?.metadata?.publishedAt || null}
                render={({ onChange, name, value }) => (
                  <DatePicker
                    id={name}
                    placeholderText="Pilih tanggal atau kosongkan"
                    dateFormat="yyyy/MM/dd"
                    selected={value ? new Date(value) : null}
                    className={`rui-datetimepicker form-control w-auto mb-1 ${!!errors.publishedAt ? 'is-invalid' : ''
                      }`}
                    onChange={onChange}
                  />
                )}
              />
            </div>
            <FormFeedback className="d-block">{errors.publishedAt?.message}</FormFeedback>
          </FormGroup>
          <FormGroup>
            <CustomInput
              id="affiliateSwitch"
              label="Produk bisa diaffiliate"
              defaultChecked={enableAffiliate}
              type="switch"
              color="primary"
              onChange={e => setEnableAffiliate(e.target.checked)}
            />
          </FormGroup>

          {enableAffiliate && (
            <FormGroup>
              <Label for="affiliateCommission">Komisi Affiliate</Label>
              <InputGroup>
                <Input
                  type="number"
                  name="affiliateCommission"
                  id="affiliateCommission"
                  defaultValue={data?.affiliateCommission}
                  invalid={errors.affiliateCommission}
                  innerRef={register({
                    min: validator.min(1),
                    max: validator.max(70)
                  })}
                />
                <InputGroupAddon addonType="prepend">
                  <InputGroupText>%</InputGroupText>
                </InputGroupAddon>
                <FormFeedback>{errors.affiliateCommission?.message}</FormFeedback>
                <small>
                  Komisi secara otomatis dipotong dari persentase total harga penjualan (setelah dipotong kode diskon
                  jika ada, biaya channel dan biaya Mayar). Komisi hanya untuk affiliator yang terdaftar pada produk
                  ini.
                </small>
              </InputGroup>
            </FormGroup>
          )}
          <Row>
            <Col>
              <SubmitButton
                size="lg"
                block
                color="secondary"
                type="button"
                onClick={onHandleSaveAsDraft}
                className="text-center h-100 align-items-center"
                text="Simpan Sebagai Draft"
                loading
                isDisabled={disableSubmitButton}
                isLoading={insertLoading || updateLoading}
              />
            </Col>
            <Col>
              <SubmitButton
                innerRef={submitElement}
                size="lg"
                block
                color="brand"
                type="submit"
                className="text-center h-100 align-items-center"
                text={`${isUpdate ? 'Update' : 'Buat'} & Publish Komik/Galeri`}
                loading
                isDisabled={disableSubmitButton}
                isLoading={insertLoading || updateLoading}
              />
            </Col>
          </Row>
        </Form>
      </ModalBody>
    </Modal>
  );
};

export default ModalComic;
